/**
 * Deterministic battle engine wrapping @pkmn/sim's Battle class.
 */

import { Battle } from "@pkmn/sim";
import type { BattleConfig, BattleAction, BattleState, PlayerId, PlayerAction, PokemonBattleState, Team } from "./types";

export function teamToSets(team: Team): any[] {
  return team.members.map((m) => ({
    name: m.name || m.species,
    species: m.species,
    ability: m.ability,
    item: m.item || "",
    moves: m.moves,
    nature: m.nature || "",
    evs: m.evs as any,
    ivs: m.ivs as any,
    level: m.level ?? 50,
    gender: m.gender || "",
    shiny: m.shiny,
    happiness: m.happiness,
  }));
}

export function scaleTeamToLevel(team: Team, level: number): any[] {
  return teamToSets(team).map((s: any) => ({ ...s, level }));
}

export function makeSeed(base: number): string {
  return [base, base + 1, base + 2, base + 3].join(",");
}

export function generateSeed(): number {
  return Math.floor(Math.random() * 0xffffffff);
}

export class BattleEngine {
  private battle: Battle;
  private seed: string;
  private config: BattleConfig;

  static create(config: BattleConfig, seedBase: number, teamA: Team, teamB: Team): BattleEngine {
    const engine = new BattleEngine(config, seedBase);
    engine.setupPlayers(teamA, teamB);
    return engine;
  }

  private constructor(config: BattleConfig, seedBase: number) {
    this.config = config;
    this.seed = makeSeed(seedBase);
    this.battle = new Battle({ formatid: "gen5doubles", seed: this.seed.split(",").map(Number) } as any);
  }

  private setupPlayers(teamA: Team, teamB: Team): void {
    const level = this.config.level || 50;
    const setsA = scaleTeamToLevel(teamA, level);
    const setsB = scaleTeamToLevel(teamB, level);
    this.battle.setPlayer("p1", { name: teamA.name || "Player 1", team: setsA } as any);
    this.battle.setPlayer("p2", { name: teamB.name || "Player 2", team: setsB } as any);
    // @pkmn/sim starts the battle automatically once both players are set.
  }

  submitActions(playerA: PlayerAction, playerB: PlayerAction): void {
    this.battle.choose(playerA.player, this.toChoice(playerA.action));
    this.battle.choose(playerB.player, this.toChoice(playerB.action));
  }

  private toChoice(action: BattleAction): string {
    switch (action.type) {
      case "move":
        return `move ${action.move}${action.target !== undefined ? ` ${action.target + 1}` : ""}`;
      case "switch":
        return `switch ${action.slot + 1}`;
      case "struggle":
        return "move struggle";
      default:
        return "pass";
    }
  }

  getState(): BattleState {
    return this.getStateForPlayer(null);
  }

  /** Get a battle state from a specific player's perspective. Opponent bench Pokemon are masked. */
  getStateForPlayer(player: PlayerId | null): BattleState {
    const sides = this.battle.sides;
    const fullState: BattleState = {
      turn: this.battle.turn,
      p1: sides[0].pokemon.map((p) => this.toPS(p as any, p.isActive)),
      p2: sides[1].pokemon.map((p) => this.toPS(p as any, p.isActive)),
      activeP1: sides[0].active.findIndex((p) => p !== null),
      activeP2: sides[1].active.findIndex((p) => p !== null),
      weather: (this.battle as any).field?.weather || null,
      ended: this.battle.ended,
      winner: this.battle.winner ? (this.battle.winner === sides[0].name ? "p1" : "p2") : null,
    };

    if (player === null) return fullState;

    const masked: BattleState = { ...fullState };
    const opponentTeam = player === "p1" ? [...fullState.p2] : [...fullState.p1];
    const activeIdx = player === "p1" ? fullState.activeP2 : fullState.activeP1;
    for (let i = 0; i < opponentTeam.length; i++) {
      if (i === activeIdx) continue;
      opponentTeam[i] = {
        ...opponentTeam[i],
        species: "???",
        nickname: "???",
        hp: 0,
        maxHp: 0,
        moves: [],
        ability: undefined,
        item: undefined,
      };
    }
    if (player === "p1") {
      masked.p2 = opponentTeam;
    } else {
      masked.p1 = opponentTeam;
    }
    return masked;
  }

  private toPS(p: any, active: boolean): PokemonBattleState {
    return {
      species: p.species?.name ?? "",
      nickname: p.name ?? "",
      level: p.level ?? 50,
      gender: p.gender,
      hp: p.hp ?? 0,
      maxHp: p.maxhp ?? 0,
      status: p.status || null,
      ability: p.ability || undefined,
      item: p.item || undefined,
      moves: p.moveSlots ? p.moveSlots.map((m: any) => m.move) : [],
      shiny: p.shiny,
      active,
    };
  }

  getConfig(): BattleConfig {
    return this.config;
  }

  getLegalActions(player: PlayerId): BattleAction[] {
    const side = player === "p1" ? this.battle.sides[0] : this.battle.sides[1];
    const active = side.active[0];
    const actions: BattleAction[] = [];
    if (!active || active.fainted) {
      for (let i = 0; i < side.pokemon.length; i++) {
        if (!side.pokemon[i].fainted) {
          actions.push({ type: "switch", slot: i });
        }
      }
      return actions;
    }
    if (active.moveSlots) {
      for (const ms of active.moveSlots) {
        if (!ms.disabled && ms.pp > 0) {
          actions.push({ type: "move", move: ms.move });
        }
      }
    }
    for (let i = 0; i < side.pokemon.length; i++) {
      if (!side.pokemon[i].fainted && side.pokemon[i] !== active) {
        actions.push({ type: "switch", slot: i });
      }
    }
    if (actions.length === 0) {
      actions.push({ type: "struggle" });
    }
    return actions;
  }

  isEnded(): boolean {
    return this.battle.ended;
  }

  getWinner(): PlayerId | null {
    if (!this.battle.winner) return null;
    return this.battle.winner === this.battle.sides[0].name ? "p1" : "p2";
  }

  getSeed(): string {
    return this.seed;
  }
}